// src/App.js — Nash by Diwan — Application complète

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AuthProvider, useAuth, API } from "./context/AuthContext";

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const injectStyles = () => {
  if (document.getElementById("nash-styles")) return;
  const s = document.createElement("style");
  s.id = "nash-styles";
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    :root{
      --teal:#00C9A7;--blue:#0066CC;--mid:#0099BB;
      --grad:linear-gradient(135deg,#00C9A7 0%,#0099BB 45%,#0066CC 100%);
      --grad-soft:linear-gradient(135deg,rgba(0,201,167,0.10),rgba(0,102,204,0.10));
      --dark:#0B0F1C;--dark2:#131929;--dark3:#1C2438;--dark4:#242E45;
      --border:rgba(0,153,187,0.18);--border2:rgba(255,255,255,0.07);
      --white:#FFFFFF;--muted:#7B8DAB;
      --success:#22C97A;--warn:#F5A623;--danger:#E53E5A;
    }
    body{font-family:'Inter',sans-serif;background:var(--dark);color:var(--white);overflow:hidden;height:100vh}
    ::-webkit-scrollbar{width:4px;height:4px}
    ::-webkit-scrollbar-track{background:transparent}
    ::-webkit-scrollbar-thumb{background:var(--dark4);border-radius:4px}
    input,select,textarea{color-scheme:dark}
    button{font-family:'Inter',sans-serif}
    @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    @keyframes slideIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
    .animate{animation:fadeIn 0.3s ease forwards}
  `;
  document.head.appendChild(s);
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const api = (path, opts = {}) => axios({ url: `${API}${path}`, ...opts }).then(r => r.data);

const NET_CONFIG = {
  linkedin:  { color: "#0A66C2", label: "LinkedIn",   short: "LI" },
  instagram: { color: "#E1306C", label: "Instagram",  short: "IG" },
  facebook:  { color: "#1877F2", label: "Facebook",   short: "FB" },
  twitter:   { color: "#1DA1F2", label: "X/Twitter",  short: "𝕏"  },
  youtube:   { color: "#FF0000", label: "YouTube",    short: "YT" },
  tiktok:    { color: "#69C9D0", label: "TikTok",     short: "TT" },
};

const ROLE_CONFIG = {
  super_admin: { label: "Super Admin", color: "var(--teal)"    },
  admin:       { label: "Admin",       color: "var(--teal)"    },
  manager:     { label: "Manager",     color: "var(--mid)"     },
  editor:      { label: "Éditeur",     color: "var(--muted)"   },
};

// ─── UI ATOMS ─────────────────────────────────────────────────────────────────
const GradLine = () => <div style={{ position:"absolute",top:0,left:0,right:0,height:2,background:"var(--grad)",borderRadius:"14px 14px 0 0" }} />;

const NetBadge = ({ net }) => {
  const c = NET_CONFIG[net] || { color:"#888", short: net?.slice(0,2).toUpperCase() };
  return <span style={{ background:c.color+"22",color:c.color,fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:5,letterSpacing:"0.04em" }}>{c.short}</span>;
};

const StatusBadge = ({ s }) => {
  const m = { scheduled:["var(--teal)","Programmé"], draft:["var(--warn)","Brouillon"], published:["var(--success)","Publié"], failed:["var(--danger)","Échoué"], cancelled:["var(--muted)","Annulé"] };
  const [color,label] = m[s] || ["var(--muted)",s];
  return <span style={{ background:color.replace(")",",0.15)").replace("var(","rgba(").replace("var(","rgba("),color,fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:20,border:`1px solid ${color}40` }}>{label}</span>;
};

const Btn = ({ children, onClick, variant="gold", size="md", disabled, style={} }) => {
  const styles = {
    gold:    { background:"var(--grad)",color:"#fff",border:"none",boxShadow:"0 4px 14px rgba(0,153,187,0.25)" },
    outline: { background:"transparent",color:"var(--teal)",border:"1px solid rgba(0,201,167,0.4)" },
    ghost:   { background:"var(--dark3)",color:"var(--muted)",border:"1px solid var(--border2)" },
    danger:  { background:"rgba(229,62,90,0.12)",color:"var(--danger)",border:"1px solid rgba(229,62,90,0.3)" },
  };
  const sizes = { sm:"6px 12px;font-size:11px", md:"9px 18px;font-size:13px", lg:"12px 24px;font-size:14px" };
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ display:"flex",alignItems:"center",gap:7,padding:size==="sm"?"6px 12px":size==="lg"?"12px 24px":"9px 18px",borderRadius:9,cursor:disabled?"not-allowed":"pointer",fontWeight:600,fontSize:size==="sm"?11:13,transition:"all 0.15s",opacity:disabled?0.6:1,...styles[variant],...style }}
      onMouseEnter={e => { if(!disabled) e.currentTarget.style.transform="translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; }}>
      {children}
    </button>
  );
};

const Input = ({ label, value, onChange, type="text", placeholder, required }) => (
  <div style={{ marginBottom:16 }}>
    {label && <label style={{ display:"block",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:7 }}>{label}</label>}
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required}
      style={{ width:"100%",background:"var(--dark3)",border:"1px solid var(--border2)",borderRadius:9,padding:"10px 14px",color:"var(--white)",fontFamily:"'Inter',sans-serif",fontSize:13,outline:"none",transition:"border-color 0.2s" }}
      onFocus={e=>e.target.style.borderColor="var(--teal)"}
      onBlur={e=>e.target.style.borderColor="var(--border2)"} />
  </div>
);

const Card = ({ children, style={} }) => (
  <div style={{ background:"var(--dark2)",border:"1px solid var(--border2)",borderRadius:14,padding:20,position:"relative",overflow:"hidden",...style }}>
    {children}
  </div>
);

const Toast = ({ msg, type="success" }) => (
  msg ? <div style={{ position:"fixed",top:20,right:20,zIndex:9999,background:type==="success"?"#0d2a1e":type==="warn"?"#2a1f0d":"#2a0d0d",border:`1px solid var(--${type==="success"?"success":type==="warn"?"warn":"danger"})`,color:`var(--${type==="success"?"success":type==="warn"?"warn":"danger"})`,padding:"12px 20px",borderRadius:10,fontWeight:600,fontSize:13,boxShadow:"0 8px 32px rgba(0,0,0,0.5)",animation:"slideIn 0.25s ease" }}>{msg}</div> : null
);

const Modal = ({ open, onClose, title, subtitle, children }) => !open ? null : (
  <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{ background:"var(--dark2)",border:"1px solid var(--border)",borderRadius:18,padding:28,width:"100%",maxWidth:440,animation:"fadeIn 0.2s ease" }}>
      <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:16,marginBottom:4 }}>{title}</div>
      {subtitle && <div style={{ fontSize:13,color:"var(--muted)",marginBottom:20 }}>{subtitle}</div>}
      {children}
    </div>
  </div>
);

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
function LoginPage() {
  const { login } = useAuth();
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); }
    catch (err) { setError(err.response?.data?.error || "Erreur de connexion"); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:20,position:"relative" }}>
      <div style={{ position:"fixed",inset:0,background:"var(--dark)" }}>
        <div style={{ position:"absolute",top:"-20%",left:"-10%",width:600,height:600,borderRadius:"50%",background:"radial-gradient(circle,rgba(0,201,167,0.07),transparent 70%)" }} />
        <div style={{ position:"absolute",bottom:"-20%",right:"-10%",width:600,height:600,borderRadius:"50%",background:"radial-gradient(circle,rgba(0,102,204,0.07),transparent 70%)" }} />
      </div>
      <div style={{ position:"relative",width:"100%",maxWidth:400,background:"var(--dark2)",border:"1px solid var(--border)",borderRadius:20,padding:36,boxShadow:"0 24px 80px rgba(0,0,0,0.5)",animation:"fadeIn 0.4s ease" }}>
        <div style={{ display:"flex",alignItems:"center",gap:12,justifyContent:"center",marginBottom:28 }}>
          <div style={{ width:44,height:44,borderRadius:12,background:"var(--grad)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:16,color:"#fff" }}>N</div>
          <div>
            <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:20,color:"var(--white)" }}>NASH</div>
            <div style={{ fontSize:10,color:"var(--teal)",fontWeight:600,letterSpacing:"0.1em" }}>by Diwan</div>
          </div>
        </div>
        <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:20,textAlign:"center",marginBottom:6 }}>Connexion</div>
        <div style={{ fontSize:13,color:"var(--muted)",textAlign:"center",marginBottom:24 }}>Accès réservé à l'équipe</div>
        {error && <div style={{ background:"rgba(229,62,90,0.12)",border:"1px solid rgba(229,62,90,0.3)",color:"var(--danger)",padding:"10px 14px",borderRadius:8,fontSize:13,marginBottom:16 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <Input label="Adresse e-mail" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="vous@diwan-consulting.com" required />
          <Input label="Mot de passe" type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required />
          <button type="submit" disabled={loading} style={{ width:"100%",padding:13,borderRadius:10,background:"var(--grad)",border:"none",color:"#fff",fontFamily:"'Montserrat',sans-serif",fontSize:14,fontWeight:700,cursor:loading?"wait":"pointer",boxShadow:"0 4px 20px rgba(0,153,187,0.3)",opacity:loading?0.7:1,marginTop:4 }}>
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </form>
        <div style={{ textAlign:"center",fontSize:11,color:"var(--muted)",marginTop:20 }}>
          Problème ? <a href="mailto:office@diwan-consulting.com" style={{ color:"var(--teal)",textDecoration:"none" }}>office@diwan-consulting.com</a>
        </div>
      </div>
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
function Sidebar({ nav, setNav, sideOpen, setSideOpen }) {
  const { user, logout, isAdmin } = useAuth();
  const navItems = [
    { id:"dashboard", label:"Dashboard",   icon:"⊞" },
    { id:"compose",   label:"Composer",    icon:"✎" },
    { id:"calendar",  label:"Calendrier",  icon:"◫" },
    { id:"inbox",     label:"Inbox",       icon:"✉", badge:8 },
    { id:"bulk",      label:"Bulk",        icon:"⋮⋮" },
    { id:"analytics", label:"Analytics",  icon:"∿" },
    { id:"clients",   label:"Clients",    icon:"◈" },
    ...(isAdmin() ? [{ id:"team", label:"Équipe", icon:"◎" }] : []),
    { id:"settings",  label:"Paramètres", icon:"⚙" },
  ];
  const W = sideOpen ? 220 : 66;
  return (
    <aside style={{ width:W,background:"var(--dark2)",borderRight:"1px solid var(--border2)",display:"flex",flexDirection:"column",transition:"width 0.25s ease",overflow:"hidden",flexShrink:0,zIndex:50 }}>
      <div style={{ padding:"18px 14px 14px",borderBottom:"1px solid var(--border2)",flexShrink:0 }}>
        <div style={{ display:"flex",alignItems:"center",gap:10,cursor:"pointer" }} onClick={()=>setSideOpen(!sideOpen)}>
          <div style={{ width:36,height:36,borderRadius:10,background:"var(--grad)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:15,color:"#fff" }}>N</div>
          {sideOpen && <div><div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:15,color:"var(--white)" }}>NASH</div><div style={{ fontSize:10,color:"var(--teal)",fontWeight:600,letterSpacing:"0.08em" }}>by Diwan</div></div>}
        </div>
      </div>
      <nav style={{ flex:1,padding:"10px 8px",overflowY:"auto",overflowX:"hidden" }}>
        {navItems.map(item => (
          <div key={item.id} onClick={()=>setNav(item.id)}
            style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 11px",borderRadius:9,cursor:"pointer",marginBottom:2,whiteSpace:"nowrap",overflow:"hidden",transition:"all 0.15s",background:nav===item.id?"var(--grad-soft)":"transparent",borderLeft:nav===item.id?"2px solid var(--teal)":"2px solid transparent",color:nav===item.id?"var(--teal)":"var(--muted)" }}>
            <span style={{ fontSize:15,flexShrink:0,width:18,textAlign:"center" }}>{item.icon}</span>
            {sideOpen && <span style={{ fontSize:13,fontWeight:500,flex:1 }}>{item.label}</span>}
            {sideOpen && item.badge && <span style={{ background:"var(--grad)",color:"#fff",fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:20 }}>{item.badge}</span>}
          </div>
        ))}
      </nav>
      <div style={{ padding:"10px 8px",borderTop:"1px solid var(--border2)",flexShrink:0 }}>
        <div style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 11px",borderRadius:9,overflow:"hidden",cursor:"pointer" }} onClick={logout}>
          <div style={{ width:32,height:32,borderRadius:"50%",background:"var(--grad)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:12,color:"#fff",flexShrink:0 }}>{user?.name?.charAt(0)}</div>
          {sideOpen && <div style={{ overflow:"hidden" }}>
            <div style={{ fontSize:12,fontWeight:700,color:"var(--white)",whiteSpace:"nowrap" }}>{user?.name}</div>
            <div style={{ fontSize:10,color:"var(--teal)",whiteSpace:"nowrap",fontWeight:500 }}>{ROLE_CONFIG[user?.role]?.label}</div>
          </div>}
        </div>
      </div>
    </aside>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ clients, toast }) {
  const [stats,    setStats]    = useState(null);
  const [posts,    setPosts]    = useState([]);
  useEffect(() => {
    api("/api/analytics/overview").then(setStats).catch(() => {});
    api("/api/posts?status=scheduled").then(d=>setPosts(d.posts||[])).catch(()=>{});
  }, []);

  const statCards = [
    { label:"Posts programmés", value: posts.length,              delta:"+12 cette semaine", up:true  },
    { label:"Clients actifs",   value: clients.length,            delta:"comptes gérés",     up:true  },
    { label:"Taux engagement",  value: stats?.metrics?.engagementRate ? stats.metrics.engagementRate+"%" : "4.8%", delta:"+0.6% ce mois", up:true },
    { label:"Portée totale",    value: stats?.metrics?.totalReach ? (stats.metrics.totalReach/1000).toFixed(0)+"K" : "128K", delta:"ce mois", up:true },
  ];

  return (
    <div className="animate">
      <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20 }}>
        {statCards.map((s,i) => (
          <Card key={i}><GradLine />
            <div style={{ fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10 }}>{s.label}</div>
            <div style={{ fontFamily:"'Montserrat',sans-serif",fontSize:30,fontWeight:800,lineHeight:1,marginBottom:6 }}>{s.value}</div>
            <div style={{ fontSize:11,color:s.up?"var(--success)":"var(--warn)" }}>{s.up?"▲":"▼"} {s.delta}</div>
          </Card>
        ))}
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 300px",gap:14 }}>
        <Card>
          <GradLine />
          <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:14,marginBottom:16 }}>Posts récents</div>
          {posts.slice(0,6).map(p => {
            const c = clients.find(x=>x.id===p.clientId);
            return (
              <div key={p.id} style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                <div style={{ width:36,height:36,borderRadius:9,background:"var(--grad)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:11,color:"#fff",flexShrink:0 }}>{c?.name?.slice(0,2).toUpperCase()||"??"}</div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontSize:12,fontWeight:600,marginBottom:2 }}>{c?.name||"Client"}</div>
                  <div style={{ fontSize:11,color:"var(--muted)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{p.content}</div>
                </div>
                <div style={{ display:"flex",flexDirection:"column",alignItems:"flex-end",gap:5,flexShrink:0 }}>
                  <NetBadge net={p.network} />
                  <StatusBadge s={p.status} />
                </div>
              </div>
            );
          })}
          {!posts.length && <div style={{ textAlign:"center",color:"var(--muted)",padding:32,fontSize:13 }}>Aucun post programmé</div>}
        </Card>
        <Card>
          <GradLine />
          <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:14,marginBottom:16 }}>Meilleurs horaires</div>
          {Object.entries({ linkedin:["08h","12h","18h"], instagram:["11h","17h","21h"], facebook:["09h","15h","20h"], youtube:["14h","18h"], tiktok:["12h","19h","22h"], twitter:["07h","13h","19h"] }).map(([net,times]) => (
            <div key={net} style={{ marginBottom:12 }}>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}><NetBadge net={net} /><span style={{ fontSize:10,color:"var(--muted)" }}>{times.join(" · ")}</span></div>
              <div style={{ height:3,background:"var(--dark4)",borderRadius:2 }}><div style={{ width:"80%",height:"100%",background:"var(--grad)",borderRadius:2 }} /></div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ─── CLIENTS PAGE ─────────────────────────────────────────────────────────────
function ClientsPage({ clients, setClients, toast }) {
  const [showCreate, setShowCreate] = useState(false);
  const [showAccounts, setShowAccounts] = useState(null); // clientId
  const [form, setForm] = useState({ name:"", color:"#00C9A7", website:"", notes:"" });
  const [accounts, setAccounts] = useState([]);

  const loadAccounts = async (clientId) => {
    try { const d = await api(`/api/clients/${clientId}/accounts`); setAccounts(d.accounts||[]); }
    catch { setAccounts([]); }
  };

  const createClient = async () => {
    if (!form.name.trim()) return toast("Nom du client requis","warn");
    try {
      const d = await api("/api/clients", { method:"POST", data: form });
      setClients(prev => [...prev, d.client]);
      setShowCreate(false); setForm({ name:"", color:"#00C9A7", website:"", notes:"" });
      toast(`Client "${d.client.name}" créé ✓`);
    } catch (err) { toast(err.response?.data?.error||"Erreur","danger"); }
  };

  const deleteClient = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" et tous ses comptes sociaux ?`)) return;
    try {
      await api(`/api/clients/${id}`, { method:"DELETE" });
      setClients(prev => prev.filter(c => c.id !== id));
      toast(`Client "${name}" supprimé`);
    } catch (err) { toast(err.response?.data?.error||"Erreur","danger"); }
  };

  const connectNetwork = (clientId, network) => {
    const token = localStorage.getItem("nash_token"); window.location.href = `${API}/auth/${network}?clientId=${clientId}&token=${token}`;
  };

  return (
    <div className="animate">
      <div style={{ display:"flex",justifyContent:"flex-end",marginBottom:16 }}>
        <Btn onClick={()=>setShowCreate(true)}>+ Ajouter un client</Btn>
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14 }}>
        {clients.map(c => (
          <Card key={c.id} style={{ cursor:"default" }}>
            <div style={{ position:"absolute",top:0,left:0,right:0,height:3,background:c.color,borderRadius:"14px 14px 0 0" }} />
            <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:14 }}>
              <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                <div style={{ width:44,height:44,borderRadius:12,background:c.color+"22",border:`1px solid ${c.color}40`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:16,color:c.color }}>{c.name.slice(0,2).toUpperCase()}</div>
                <div><div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:15 }}>{c.name}</div>{c.website&&<div style={{ fontSize:11,color:"var(--teal)",marginTop:2 }}>{c.website}</div>}</div>
              </div>
            </div>
            <div style={{ display:"flex",gap:5,flexWrap:"wrap",marginBottom:14 }}>
              {(c.socialAccounts||[]).map(a => <NetBadge key={a.id} net={a.network} />)}
              {!(c.socialAccounts||[]).length && <span style={{ fontSize:11,color:"var(--muted)" }}>Aucun compte connecté</span>}
            </div>
            <div style={{ display:"flex",gap:8 }}>
              <Btn size="sm" variant="outline" onClick={()=>{ setShowAccounts(c.id); loadAccounts(c.id); }}>Gérer réseaux</Btn>
              <Btn size="sm" variant="danger" onClick={()=>deleteClient(c.id,c.name)}>Supprimer</Btn>
            </div>
          </Card>
        ))}
        {!clients.length && (
          <div style={{ gridColumn:"1/-1",textAlign:"center",padding:48,color:"var(--muted)" }}>
            <div style={{ fontSize:32,marginBottom:12 }}>◈</div>
            <div style={{ fontSize:14,fontWeight:600,marginBottom:6 }}>Aucun client</div>
            <div style={{ fontSize:12 }}>Cliquez sur "Ajouter un client" pour commencer</div>
          </div>
        )}
      </div>

      {/* Modal créer client */}
      <Modal open={showCreate} onClose={()=>setShowCreate(false)} title="Nouveau client" subtitle="Créez un profil client pour gérer ses réseaux sociaux.">
        <Input label="Nom du client" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="TL Fiduciaire" required />
        <Input label="Site web (optionnel)" value={form.website} onChange={e=>setForm({...form,website:e.target.value})} placeholder="https://..." />
        <div style={{ marginBottom:16 }}>
          <label style={{ display:"block",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:7 }}>Couleur</label>
          <input type="color" value={form.color} onChange={e=>setForm({...form,color:e.target.value})} style={{ width:60,height:36,borderRadius:8,border:"1px solid var(--border2)",background:"var(--dark3)",cursor:"pointer" }} />
        </div>
        <div style={{ display:"flex",gap:10 }}>
          <Btn variant="ghost" onClick={()=>setShowCreate(false)} style={{ flex:1,justifyContent:"center" }}>Annuler</Btn>
          <Btn onClick={createClient} style={{ flex:1,justifyContent:"center" }}>Créer</Btn>
        </div>
      </Modal>

      {/* Modal gérer comptes sociaux */}
      <Modal open={!!showAccounts} onClose={()=>setShowAccounts(null)} title="Comptes sociaux" subtitle="Connectez les réseaux sociaux de ce client.">
        <div style={{ marginBottom:16 }}>
          {accounts.length ? accounts.map(a => (
            <div key={a.id} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
              <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                <NetBadge net={a.network} />
                <span style={{ fontSize:12,color:"var(--white)" }}>{a.profileName||a.profileId}</span>
              </div>
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <div style={{ width:7,height:7,borderRadius:"50%",background:"var(--success)" }} />
                <Btn size="sm" variant="danger" onClick={async()=>{ await api(`/api/clients/${showAccounts}/accounts/${a.id}`,{method:"DELETE"}); loadAccounts(showAccounts); toast("Compte déconnecté"); }}>Retirer</Btn>
              </div>
            </div>
          )) : <div style={{ textAlign:"center",color:"var(--muted)",padding:"16px 0",fontSize:12 }}>Aucun compte connecté</div>}
        </div>
        <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:600,fontSize:12,marginBottom:12,color:"var(--muted)" }}>CONNECTER UN RÉSEAU</div>
        <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
          {Object.entries(NET_CONFIG).map(([net,cfg]) => (
            <Btn key={net} size="sm" variant="ghost" onClick={()=>connectNetwork(showAccounts,net==="twitter"?"twitter":net==="youtube"?"youtube":net==="tiktok"?"tiktok":net==="meta"?"meta":net)}
              style={{ borderColor:cfg.color+"40",color:cfg.color }}>
              {cfg.short} {cfg.label}
            </Btn>
          ))}
        </div>
      </Modal>
    </div>
  );
}

// ─── COMPOSE PAGE ─────────────────────────────────────────────────────────────
function ComposePage({ clients, toast }) {
  const [selNets,  setSelNets]  = useState(["linkedin"]);
  const [clientId, setClientId] = useState("");
  const [content,  setContent]  = useState("");
  const [schedDt,  setSchedDt]  = useState("");
  const [aiLoad,   setAiLoad]   = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishMsg, setPublishMsg] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [linkedInTarget, setLinkedInTarget] = useState('auto');
  const [linkedInPages, setLinkedInPages] = useState([]);

  const toggleNet = (n) => setSelNets(p => p.includes(n) ? p.filter(x=>x!==n) : [...p,n]);

  // Charge les pages LinkedIn du client sélectionné
  const loadLinkedInPages = async (cid) => {
    if (!cid) return;
    try {
      const d = await api('/api/clients/' + cid + '/accounts');
      const liAcc = (d.accounts || []).find(a => a.network === 'linkedin');
      if (liAcc && liAcc.extra && liAcc.extra.pages) {
        setLinkedInPages(liAcc.extra.pages);
        setLinkedInTarget(liAcc.extra.defaultPageId || 'profile');
      } else {
        setLinkedInPages([]);
        setLinkedInTarget('profile');
      }
    } catch { setLinkedInPages([]); }
  };

  const genAI = async () => {
    if (!content && !clientId) { toast("Sélectionnez un client et un réseau","warn"); return; }
    setAiLoad(true);
    const c = clients.find(x=>x.id===clientId);
    const nets = selNets.map(n=>NET_CONFIG[n]?.label).join(", ");
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens:400, messages:[{ role:"user", content:`Expert marketing digital. Rédige un post ${nets} pour ${c?.name||"cette entreprise"}. Professionnel, accrocheur, en français. Max 180 mots. Retourne uniquement le texte du post.` }] }) });
      const data = await res.json();
      setContent(data.content?.find(b=>b.type==="text")?.text||"");
      toast("✦ Contenu généré ✓");
    } catch { toast("Erreur IA","danger"); }
    setAiLoad(false);
  };

  const schedule = async () => {
    if (!clientId || !content || !schedDt) { toast("Client, contenu et date requis","warn"); return; }
    try {
      await api("/api/posts/schedule", { method:"POST", data:{ clientId, network: selNets, content, scheduledAt: schedDt } });
      toast(`${selNets.length} post(s) programmé(s) ✓`);
      setContent(""); setSchedDt("");
    } catch (err) { toast(err.response?.data?.error||"Erreur","danger"); }
  };

  const publishNow = async () => {
    if (!clientId || !content) { toast("Client et contenu requis","warn"); return; }
    const isVideo = imageFile && ["video/mp4","video/mov","video/avi","video/quicktime"].includes(imageFile.type);
    setPublishing(true);
    setPublishMsg(isVideo ? "⏳ Upload vidéo en cours, patientez..." : imageFile ? "⏳ Upload image en cours..." : "⏳ Publication en cours...");
    try {
      const fd = new FormData();
      fd.append("clientId", clientId);
      fd.append("content", content);
      selNets.forEach(n => fd.append("network", n));
      if (imageFile) fd.append("media", imageFile);
      const token = localStorage.getItem("nash_token");
      const res = await fetch(`${API}/api/posts/publish-now`, { method:"POST", headers:{ Authorization:`Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erreur publication");
      const success = data.results?.filter(r => r.success).length || 0;
      const failed  = data.results?.filter(r => !r.success).length || 0;
      if (success > 0) toast(`✓ Publié sur ${success} réseau(x)`);
      if (failed  > 0) toast(`✗ Échec sur ${failed} réseau(x) — vérifiez les connexions`, "danger");
      setContent(""); setImageFile(null); setImagePreview(null); setPublishing(false); setPublishMsg("");
    } catch (err) { toast(err.message||"Erreur publication","danger"); setPublishing(false); setPublishMsg(""); }
  };
  return (
    <div className="animate" style={{ maxWidth:760,margin:"0 auto" }}>
      <Card>
        <GradLine />
        <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:16,marginBottom:22 }}>Créer un post</div>

        <div style={{ marginBottom:18 }}>
          <label style={{ display:"block",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10 }}>Réseaux sociaux</label>
          <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
            {Object.entries(NET_CONFIG).map(([n,cfg]) => (
              <div key={n} onClick={()=>toggleNet(n)}
                style={{ display:"flex",alignItems:"center",gap:7,padding:"7px 14px",borderRadius:9,border:`1px solid ${selNets.includes(n)?cfg.color:"var(--border2)"}`,cursor:"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s",background:selNets.includes(n)?cfg.color+"22":"var(--dark3)",color:selNets.includes(n)?cfg.color:"var(--muted)" }}>
                {cfg.short} {cfg.label}
                {selNets.includes(n) && <span style={{ color:cfg.color,fontSize:12 }}>✓</span>}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:18 }}>
          <div>
            <label style={{ display:"block",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>Client</label>
            <select value={clientId} onChange={e=>{ setClientId(e.target.value); loadLinkedInPages(e.target.value); }} style={{ width:"100%",background:"var(--dark3)",border:"1px solid var(--border2)",borderRadius:9,padding:"10px 14px",color:"var(--white)",fontSize:13,outline:"none" }}>
              <option value="">— Sélectionner un client —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display:"block",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>Date & heure</label>
            <input type="datetime-local" value={schedDt} onChange={e=>setSchedDt(e.target.value)} style={{ width:"100%",background:"var(--dark3)",border:"1px solid var(--border2)",borderRadius:9,padding:"10px 14px",color:"var(--white)",fontSize:13,outline:"none" }} />
          </div>
        </div>

        {selNets.includes('linkedin') && linkedInPages.length > 0 && (
          <div style={{ marginBottom:18 }}>
            <label style={{ display:"block",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>Publier LinkedIn en tant que</label>
            <select value={linkedInTarget} onChange={e=>setLinkedInTarget(e.target.value)} style={{ width:"100%",background:"var(--dark3)",border:"1px solid var(--border2)",borderRadius:9,padding:"10px 14px",color:"var(--white)",fontSize:13,outline:"none" }}>
              <option value="profile">👤 Profil personnel</option>
              {linkedInPages.map(p => <option key={p.id} value={p.id}>🏢 {p.name}</option>)}
            </select>
          </div>
        )}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:"block",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>Contenu</label>
          <textarea value={content} onChange={e=>setContent(e.target.value)} rows={6} placeholder="Rédigez votre contenu, ou générez-le avec l'IA ✦"
            style={{ width:"100%",background:"var(--dark3)",border:"1px solid var(--border2)",borderRadius:10,padding:14,color:"var(--white)",fontFamily:"'Inter',sans-serif",fontSize:14,resize:"vertical",outline:"none",lineHeight:1.7,transition:"border-color 0.2s" }}
            onFocus={e=>e.target.style.borderColor="var(--teal)"}
            onBlur={e=>e.target.style.borderColor="var(--border2)"} />
          <div style={{ fontSize:11,color:content.length>2800?"var(--warn)":"var(--muted)",textAlign:"right",marginTop:4 }}>{content.length}/3000</div>
        </div>

        <div style={{ padding:16,background:"var(--grad-soft)",border:"1px solid var(--border)",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20 }}>
          <div style={{ display:"flex",alignItems:"center",gap:10 }}>
            <div style={{ width:32,height:32,borderRadius:8,background:"var(--grad)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15 }}>⚡</div>
            <div><div style={{ fontSize:13,fontWeight:600 }}>Générer avec l'IA Claude</div><div style={{ fontSize:11,color:"var(--muted)" }}>Optimisé pour chaque réseau sélectionné</div></div>
          </div>
          <Btn onClick={genAI} disabled={aiLoad}>{aiLoad?"Génération...":"✦ Générer"}</Btn>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={{ display:"block",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>Visuel (optionnel)</label>
          <div style={{ display:"flex",alignItems:"center",gap:12 }}>
            <label style={{ display:"flex",alignItems:"center",gap:8,padding:"9px 18px",borderRadius:9,background:"var(--dark3)",border:"1px solid var(--border2)",color:"var(--muted)",fontSize:13,fontWeight:600,cursor:"pointer",transition:"all 0.15s" }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--teal)";e.currentTarget.style.color="var(--teal)";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border2)";e.currentTarget.style.color="var(--muted)";}}>
              <span style={{fontSize:16}}>🖼</span> Choisir une image
              <input type="file" accept="image/*,video/*" style={{display:"none"}} onChange={e=>{
                const f=e.target.files[0];
                if(f){setImageFile(f);const r=new FileReader();r.onload=ev=>setImagePreview(ev.target.result);r.readAsDataURL(f);}
              }}/>
            </label>
            {imagePreview && <div style={{position:"relative"}}>
              <img src={imagePreview} alt="preview" style={{width:60,height:60,objectFit:"cover",borderRadius:8,border:"1px solid var(--border)"}}/>
              <div onClick={()=>{setImageFile(null);setImagePreview(null);}} style={{position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:"50%",background:"var(--danger)",color:"#fff",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontWeight:700}}>×</div>
            </div>}
          </div>
        </div>


        <div style={{ display:"flex",gap:10 }}>
          <Btn onClick={schedule} style={{ flex:1,justifyContent:"center" }}>⏱ Programmer</Btn>
          <Btn variant="outline" onClick={publishNow} disabled={publishing} style={{ justifyContent:"center", opacity: publishing ? 0.7 : 1 }}>{publishing ? publishMsg : "↑ Publier maintenant"}</Btn>
          <Btn variant="ghost" onClick={()=>toast("Brouillon sauvegardé","warn")} style={{ justifyContent:"center" }}>Brouillon</Btn>
        </div>
      </Card>
    </div>
  );
}

// ─── CALENDAR PAGE ────────────────────────────────────────────────────────────
function CalendarPage({ clients }) {
  const [posts,  setPosts]  = useState([]);
  const [month,  setMonth]  = useState(new Date());

  useEffect(() => {
    api("/api/posts").then(d=>setPosts(d.posts||[])).catch(()=>{});
  }, []);

  const days = Array.from({ length: 42 }, (_, i) => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const start = new Date(first); start.setDate(1 - (first.getDay()||7) + 1);
    const d = new Date(start); d.setDate(d.getDate() + i);
    return d;
  });

  const postsForDay = (d) => posts.filter(p => {
    if (!p.scheduledAt) return false;
    const pd = new Date(p.scheduledAt);
    return pd.getDate()===d.getDate() && pd.getMonth()===d.getMonth() && pd.getFullYear()===d.getFullYear();
  });

  const today = new Date();
  const monthNames = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

  return (
    <div className="animate">
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18 }}>
        <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:20 }}>
          {monthNames[month.getMonth()]} <span style={{ background:"var(--grad)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>{month.getFullYear()}</span>
        </div>
        <div style={{ display:"flex",gap:8 }}>
          <Btn size="sm" variant="ghost" onClick={()=>setMonth(new Date(month.getFullYear(),month.getMonth()-1))}>◀</Btn>
          <Btn size="sm" onClick={()=>setMonth(new Date())}>Aujourd'hui</Btn>
          <Btn size="sm" variant="ghost" onClick={()=>setMonth(new Date(month.getFullYear(),month.getMonth()+1))}>▶</Btn>
        </div>
      </div>
      <Card style={{ padding:0 }}>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",borderBottom:"1px solid var(--border2)" }}>
          {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d=><div key={d} style={{ padding:"12px 8px",textAlign:"center",fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em" }}>{d}</div>)}
        </div>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)" }}>
          {days.map((d,i) => {
            const isThisMonth = d.getMonth() === month.getMonth();
            const isToday = d.toDateString() === today.toDateString();
            const dp = postsForDay(d);
            return (
              <div key={i} style={{ minHeight:90,padding:7,borderBottom:"1px solid rgba(255,255,255,0.03)",borderRight:"1px solid rgba(255,255,255,0.03)",background:isToday?"rgba(0,201,167,0.07)":"transparent",opacity:isThisMonth?1:0.35 }}>
                <div style={{ fontSize:12,fontWeight:isToday?800:500,color:isToday?"var(--teal)":"var(--muted)",marginBottom:5 }}>{d.getDate()}</div>
                {dp.map((p,pi) => {
                  const cfg = NET_CONFIG[p.network]||{color:"#888"};
                  const c   = clients.find(x=>x.id===p.clientId);
                  return <div key={pi} style={{ background:cfg.color+"25",color:cfg.color,fontSize:9,fontWeight:700,padding:"2px 5px",borderRadius:4,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",border:`1px solid ${cfg.color}40` }}>{cfg.short} · {c?.name||"?"}</div>;
                })}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ─── ANALYTICS PAGE ───────────────────────────────────────────────────────────
function AnalyticsPage({ clients, toast }) {
  const [period, setPeriod] = useState("30 jours");
  const [data,   setData]   = useState(null);

  useEffect(() => { api("/api/analytics/overview").then(setData).catch(()=>{}); }, []);

  return (
    <div className="animate">
      <div style={{ display:"flex",justifyContent:"flex-end",gap:8,marginBottom:18 }}>
        {["7 jours","30 jours","3 mois","1 an"].map(p=><Btn key={p} size="sm" variant={period===p?"gold":"ghost"} onClick={()=>setPeriod(p)}>{p}</Btn>)}
        <Btn size="sm" variant="ghost" onClick={()=>toast("Rapport exporté ✓")}>↓ Exporter</Btn>
        <Btn size="sm" variant="ghost" onClick={()=>toast("Rapport envoyé ✓")}>✉ Email</Btn>
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:18 }}>
        {[["Portée","128.4K","+18%"],["Impressions","342K","+24%"],["Engagements","9,821","+11%"],["Abonnés +","1,240","+6%"]].map(([l,v,d],i)=>(
          <Card key={i}><GradLine />
            <div style={{ fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8 }}>{l}</div>
            <div style={{ fontFamily:"'Montserrat',sans-serif",fontSize:28,fontWeight:800 }}>{v}</div>
            <div style={{ fontSize:11,color:"var(--success)",marginTop:5 }}>▲ {d}</div>
          </Card>
        ))}
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
        <Card><GradLine />
          <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:14,marginBottom:18 }}>Performance par réseau</div>
          {[["linkedin",92,"34.2K"],["instagram",78,"28.1K"],["facebook",65,"21.4K"],["youtube",88,"31.0K"],["tiktok",94,"13.7K"],["twitter",55,"9.4K"]].map(([n,s,r])=>(
            <div key={n} style={{ marginBottom:14 }}>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}><NetBadge net={n} /><span style={{ fontSize:11,color:"var(--muted)" }}>{r}</span></div>
              <div style={{ height:5,background:"var(--dark4)",borderRadius:4 }}><div style={{ width:s+"%",height:"100%",background:"var(--grad)",borderRadius:4 }} /></div>
            </div>
          ))}
        </Card>
        <Card><GradLine />
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18 }}>
            <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:14 }}>Rapports programmés</div>
            <Btn size="sm" onClick={()=>toast("Rapport créé ✓")}>+ Nouveau</Btn>
          </div>
          {[["Rapport mensuel Diwan","Mensuel","office@diwan-consulting.com","01/04"],["Analytics TL Fiduciaire","Hebdomadaire","tl@fiduciaire.ch","22/03"],["Performance K5 Arena","Mensuel","k@k5arena.com","01/04"]].map(([n,f,e,d],i,arr)=>(
            <div key={i} style={{ padding:"12px 0",borderBottom:i<arr.length-1?"1px solid rgba(255,255,255,0.05)":"none" }}>
              <div style={{ fontSize:13,fontWeight:600,marginBottom:4 }}>{n}</div>
              <div style={{ fontSize:11,color:"var(--muted)",display:"flex",gap:10 }}><span style={{ color:"var(--teal)",fontWeight:600 }}>{f}</span><span>{e}</span><span>→ {d}</span></div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ─── TEAM PAGE (admin only) ───────────────────────────────────────────────────
function TeamPage({ toast }) {
  const { user } = useAuth();
  const [team,       setTeam]       = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showReset,  setShowReset]  = useState(null);
  const [form,       setForm]       = useState({ name:"", email:"", password:"", role:"editor" });
  const [newPwd,     setNewPwd]     = useState("");

  const load = useCallback(() => { api("/api/users").then(d=>setTeam(d.users||[])).catch(()=>{}); }, []);
  useEffect(()=>load(),[load]);

  const create = async () => {
    if (!form.name||!form.email||!form.password) return toast("Tous les champs sont requis","warn");
    if (form.password.length<8) return toast("8 caractères minimum","warn");
    try {
      const d = await api("/api/users", { method:"POST", data:form });
      setTeam(prev=>[...prev, d.user]); setShowCreate(false); setForm({ name:"",email:"",password:"",role:"editor" });
      toast(`Compte créé pour ${d.user.name} ✓`);
    } catch (err) { toast(err.response?.data?.error||"Erreur","danger"); }
  };

  const toggle = async (id) => {
    try { const d = await api(`/api/users/${id}/toggle`,{method:"PUT"}); load(); toast(d.active?"Compte activé ✓":"Compte désactivé"); }
    catch (err) { toast(err.response?.data?.error||"Erreur","danger"); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer le compte de ${name} ?`)) return;
    try { await api(`/api/users/${id}`,{method:"DELETE"}); setTeam(p=>p.filter(u=>u.id!==id)); toast(`Compte de ${name} supprimé`); }
    catch (err) { toast(err.response?.data?.error||"Erreur","danger"); }
  };

  const resetPwd = async () => {
    if (!newPwd||newPwd.length<8) return toast("8 caractères minimum","warn");
    try { await api(`/api/users/${showReset.id}/reset-password`,{method:"PUT",data:{newPassword:newPwd}}); setShowReset(null); setNewPwd(""); toast("Mot de passe réinitialisé ✓"); }
    catch (err) { toast(err.response?.data?.error||"Erreur","danger"); }
  };

  return (
    <div className="animate">
      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20 }}>
        {[["Membres actifs", team.filter(u=>u.active).length],["Managers",team.filter(u=>u.role==="manager").length],["Éditeurs",team.filter(u=>u.role==="editor").length]].map(([l,v],i)=>(
          <Card key={i}><GradLine /><div style={{ fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>{l}</div><div style={{ fontFamily:"'Montserrat',sans-serif",fontSize:28,fontWeight:800 }}>{v}</div></Card>
        ))}
      </div>
      <Card style={{ padding:0 }}>
        <div style={{ padding:"16px 22px",borderBottom:"1px solid var(--border2)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <span style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:14 }}>Équipe Nash</span>
          <Btn onClick={()=>setShowCreate(true)}>+ Créer un accès</Btn>
        </div>
        <table style={{ width:"100%",borderCollapse:"collapse" }}>
          <thead><tr>{["Utilisateur","Rôle","Statut","Dernière connexion","Actions"].map(h=><th key={h} style={{ padding:"12px 22px",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.07em",textAlign:"left",borderBottom:"1px solid var(--border2)" }}>{h}</th>)}</tr></thead>
          <tbody>
            {team.map(u=>(
              <tr key={u.id}>
                <td style={{ padding:"14px 22px",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                  <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                    <div style={{ width:36,height:36,borderRadius:"50%",background:"var(--grad)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:13,color:"#fff",flexShrink:0 }}>{u.name.charAt(0)}</div>
                    <div><div style={{ fontSize:13,fontWeight:600 }}>{u.name}{u.id==="super-admin-001"&&<span style={{ fontSize:10,color:"var(--teal)",marginLeft:6 }}>● Super Admin</span>}</div><div style={{ fontSize:11,color:"var(--muted)" }}>{u.email}</div></div>
                  </div>
                </td>
                <td style={{ padding:"14px 22px",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                  <span style={{ background:(ROLE_CONFIG[u.role]?.color||"var(--muted)")+"22",color:ROLE_CONFIG[u.role]?.color||"var(--muted)",fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:20,border:`1px solid ${ROLE_CONFIG[u.role]?.color||"var(--muted)"}40` }}>{ROLE_CONFIG[u.role]?.label||u.role}</span>
                </td>
                <td style={{ padding:"14px 22px",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                  <span style={{ display:"flex",alignItems:"center",gap:6,fontSize:12 }}><span style={{ width:7,height:7,borderRadius:"50%",background:u.active?"var(--success)":"var(--danger)",display:"inline-block" }} />{u.active?"Actif":"Désactivé"}</span>
                </td>
                <td style={{ padding:"14px 22px",borderBottom:"1px solid rgba(255,255,255,0.04)",fontSize:11,color:"var(--muted)" }}>
                  {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString("fr-FR",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : "Jamais"}
                </td>
                <td style={{ padding:"14px 22px",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                  {u.id!=="super-admin-001"&&(
                    <div style={{ display:"flex",gap:8 }}>
                      <Btn size="sm" variant="ghost" onClick={()=>setShowReset(u)}>Mot de passe</Btn>
                      <Btn size="sm" variant="ghost" onClick={()=>toggle(u.id)}>{u.active?"Désactiver":"Activer"}</Btn>
                      <Btn size="sm" variant="danger" onClick={()=>remove(u.id,u.name)}>Supprimer</Btn>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={showCreate} onClose={()=>setShowCreate(false)} title="Créer un accès" subtitle="Le collaborateur recevra ses identifiants de votre part.">
        <Input label="Nom complet" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Sara Leroy" />
        <Input label="Adresse e-mail" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="sara@diwan-consulting.com" />
        <Input label="Mot de passe temporaire" type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Min. 8 caractères" />
        <div style={{ marginBottom:16 }}>
          <label style={{ display:"block",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:7 }}>Rôle</label>
          <select value={form.role} onChange={e=>setForm({...form,role:e.target.value})} style={{ width:"100%",background:"var(--dark3)",border:"1px solid var(--border2)",borderRadius:9,padding:"10px 14px",color:"var(--white)",fontSize:13,outline:"none" }}>
            <option value="editor">Éditeur — posts et programmation</option>
            <option value="manager">Manager — accès complet sauf admin</option>
          </select>
        </div>
        <div style={{ display:"flex",gap:10 }}>
          <Btn variant="ghost" onClick={()=>setShowCreate(false)} style={{ flex:1,justifyContent:"center" }}>Annuler</Btn>
          <Btn onClick={create} style={{ flex:1,justifyContent:"center" }}>Créer le compte</Btn>
        </div>
      </Modal>

      <Modal open={!!showReset} onClose={()=>setShowReset(null)} title="Réinitialiser le mot de passe" subtitle={`Nouveau mot de passe pour ${showReset?.name}`}>
        <Input label="Nouveau mot de passe" type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)} placeholder="Min. 8 caractères" />
        <div style={{ display:"flex",gap:10 }}>
          <Btn variant="ghost" onClick={()=>setShowReset(null)} style={{ flex:1,justifyContent:"center" }}>Annuler</Btn>
          <Btn onClick={resetPwd} style={{ flex:1,justifyContent:"center" }}>Réinitialiser</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ─── INBOX PAGE ───────────────────────────────────────────────────────────────
function InboxPage({ clients, toast }) {
  const [messages, setMessages] = useState([]);
  const [selMsg,   setSelMsg]   = useState(null);
  const [reply,    setReply]    = useState("");

  useEffect(() => { api("/api/inbox").then(d=>{ setMessages(d.messages||[]); if(d.messages?.length) setSelMsg(d.messages[0]); }).catch(()=>{}); }, []);

  const sendReply = async () => {
    if (!reply.trim()||!selMsg) return;
    try { await api("/api/inbox/reply",{method:"POST",data:{ network:selMsg.network,convId:selMsg.convId,pageToken:selMsg.pageToken,message:reply }}); setReply(""); toast("Réponse envoyée ✓"); }
    catch { toast("Erreur envoi","danger"); }
  };

  return (
    <div className="animate" style={{ display:"grid",gridTemplateColumns:"300px 1fr",gap:14,height:"calc(100vh - 190px)" }}>
      <Card style={{ padding:0,overflow:"hidden",display:"flex",flexDirection:"column" }}>
        <div style={{ padding:"14px 16px",borderBottom:"1px solid var(--border2)",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0 }}>
          <span style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:13 }}>Messages</span>
          <span style={{ background:"var(--grad)",color:"#fff",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20 }}>{messages.filter(m=>m.unread).length||0}</span>
        </div>
        <div style={{ overflowY:"auto",flex:1 }}>
          {messages.length ? messages.map(m=>(
            <div key={m.id} onClick={()=>setSelMsg(m)}
              style={{ padding:"13px 16px",borderBottom:"1px solid rgba(255,255,255,0.04)",cursor:"pointer",background:selMsg?.id===m.id?"var(--grad-soft)":"transparent",borderLeft:selMsg?.id===m.id?"2px solid var(--teal)":"2px solid transparent",transition:"all 0.15s" }}>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:4 }}>
                <div style={{ display:"flex",alignItems:"center",gap:7 }}>
                  {m.unread&&<div style={{ width:7,height:7,borderRadius:"50%",background:"var(--teal)",flexShrink:0 }} />}
                  <span style={{ fontSize:13,fontWeight:m.unread?700:500 }}>{m.from}</span>
                </div>
                <span style={{ fontSize:10,color:"var(--muted)" }}>{m.time}</span>
              </div>
              <div style={{ fontSize:11,color:"var(--muted)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:6 }}>{m.preview}</div>
              <div style={{ display:"flex",gap:6,alignItems:"center" }}><NetBadge net={m.network} /><span style={{ fontSize:10,color:"var(--muted)" }}>{clients.find(c=>c.id===m.clientId)?.name||"Client"}</span></div>
            </div>
          )) : <div style={{ textAlign:"center",color:"var(--muted)",padding:32,fontSize:12 }}>Aucun message</div>}
        </div>
      </Card>
      <Card style={{ padding:0,display:"flex",flexDirection:"column",overflow:"hidden" }}>
        {selMsg ? <>
          <div style={{ padding:"14px 18px",borderBottom:"1px solid var(--border2)",display:"flex",alignItems:"center",gap:12,flexShrink:0 }}>
            <div style={{ width:40,height:40,borderRadius:"50%",background:"var(--grad)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:14,color:"#fff",flexShrink:0 }}>{selMsg.from.charAt(0)}</div>
            <div style={{ flex:1 }}><div style={{ fontWeight:700,fontSize:14 }}>{selMsg.from}</div><div style={{ fontSize:11,color:"var(--muted)",display:"flex",gap:8,marginTop:2,alignItems:"center" }}><NetBadge net={selMsg.network} /><span>{clients.find(c=>c.id===selMsg.clientId)?.name}</span></div></div>
            <div style={{ display:"flex",gap:8 }}>
              <Btn size="sm" variant="ghost" onClick={()=>toast("Assigné ✓")}>👤 Assigner</Btn>
              <Btn size="sm" variant="ghost" onClick={()=>toast("Tagué ✓")}>🏷 Taguer</Btn>
            </div>
          </div>
          <div style={{ flex:1,padding:18,overflowY:"auto" }}>
            <div style={{ background:"var(--dark3)",borderRadius:12,padding:"14px 16px",maxWidth:"75%",border:"1px solid var(--border2)" }}>
              <div style={{ fontSize:13,lineHeight:1.65 }}>{selMsg.preview}</div>
              <div style={{ fontSize:10,color:"var(--muted)",marginTop:8 }}>{selMsg.time}</div>
            </div>
          </div>
          <div style={{ padding:"13px 18px",borderTop:"1px solid var(--border2)",flexShrink:0,display:"flex",gap:10 }}>
            <textarea value={reply} onChange={e=>setReply(e.target.value)} placeholder="Répondre..." rows={2} style={{ flex:1,background:"var(--dark3)",border:"1px solid var(--border2)",borderRadius:9,padding:"10px 14px",color:"var(--white)",fontFamily:"'Inter',sans-serif",fontSize:13,resize:"none",outline:"none" }} onFocus={e=>e.target.style.borderColor="var(--teal)"} onBlur={e=>e.target.style.borderColor="var(--border2)"} />
            <Btn onClick={sendReply} style={{ padding:"0 18px",alignSelf:"stretch" }}>↑</Btn>
          </div>
        </> : <div style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--muted)" }}>Sélectionnez un message</div>}
      </Card>
    </div>
  );
}

// ─── BULK PAGE ────────────────────────────────────────────────────────────────
function BulkPage({ toast }) {
  return (
    <div className="animate" style={{ maxWidth:680,margin:"0 auto" }}>
      <Card><GradLine />
        <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:16,marginBottom:6 }}>Bulk Scheduling</div>
        <div style={{ fontSize:13,color:"var(--muted)",marginBottom:24 }}>Importez jusqu'à <strong style={{ color:"var(--teal)" }}>350 posts</strong> en une seule fois via CSV.</div>
        <div style={{ border:"2px dashed var(--border)",borderRadius:14,padding:"44px 24px",textAlign:"center",cursor:"pointer",marginBottom:22,background:"var(--dark3)",transition:"all 0.2s" }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--teal)";e.currentTarget.style.background="rgba(0,201,167,0.04)";}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.background="var(--dark3)";}}>
          <div style={{ fontSize:36,marginBottom:12 }}>↑</div>
          <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:15,marginBottom:6 }}>Glissez votre fichier CSV ici</div>
          <div style={{ fontSize:12,color:"var(--muted)",marginBottom:14 }}>ou cliquez pour sélectionner (max 350 lignes)</div>
          <Btn onClick={()=>toast("Fonctionnalité disponible après déploiement backend","warn")}>Parcourir</Btn>
        </div>
        <div style={{ background:"var(--dark3)",borderRadius:12,padding:18 }}>
          <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:13,marginBottom:12,color:"var(--teal)" }}>Format CSV</div>
          <div style={{ fontFamily:"monospace",fontSize:11,color:"#7fd8c8",background:"var(--dark)",padding:"13px 16px",borderRadius:8,lineHeight:1.9,border:"1px solid var(--border)" }}>
            date,heure,reseau,client_id,contenu,image_url<br/>
            2026-04-01,09:00,linkedin,CLIENT_ID,Votre contenu,<br/>
            2026-04-01,14:00,instagram,CLIENT_ID,Contenu IG,https://img.jpg
          </div>
          <div style={{ marginTop:14 }}><Btn size="sm" variant="outline" onClick={()=>{ window.open(`${API}/api/bulk/template`,"_blank"); toast("Template téléchargé ✓"); }}>↓ Télécharger le template</Btn></div>
        </div>
      </Card>
    </div>
  );
}

// ─── SETTINGS PAGE ────────────────────────────────────────────────────────────
function SettingsPage({ toast }) {
  const { user } = useAuth();
  const [pwd, setPwd] = useState({ current:"", new:"", confirm:"" });

  const changePwd = async () => {
    if (!pwd.current||!pwd.new) return toast("Champs requis","warn");
    if (pwd.new !== pwd.confirm) return toast("Les mots de passe ne correspondent pas","warn");
    if (pwd.new.length < 8) return toast("8 caractères minimum","warn");
    try { await api("/api/users/change-password",{method:"POST",data:{currentPassword:pwd.current,newPassword:pwd.new}}); setPwd({current:"",new:"",confirm:""}); toast("Mot de passe mis à jour ✓"); }
    catch (err) { toast(err.response?.data?.error||"Erreur","danger"); }
  };

  return (
    <div className="animate" style={{ maxWidth:640,margin:"0 auto" }}>
      <Card style={{ marginBottom:14 }}><GradLine />
        <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:14,marginBottom:18 }}>Mon profil</div>
        <div style={{ display:"flex",alignItems:"center",gap:16,marginBottom:20 }}>
          <div style={{ width:56,height:56,borderRadius:"50%",background:"var(--grad)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:20,color:"#fff" }}>{user?.name?.charAt(0)}</div>
          <div>
            <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:18 }}>{user?.name}</div>
            <div style={{ fontSize:13,color:"var(--muted)",marginTop:3 }}>{user?.email}</div>
            <span style={{ background:"var(--grad-soft)",color:"var(--teal)",fontSize:11,padding:"2px 10px",borderRadius:20,fontWeight:700,border:"1px solid var(--teal)",marginTop:6,display:"inline-block" }}>{ROLE_CONFIG[user?.role]?.label}</span>
          </div>
        </div>
      </Card>
      <Card><GradLine />
        <div style={{ fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:14,marginBottom:18 }}>Changer mon mot de passe</div>
        <Input label="Mot de passe actuel" type="password" value={pwd.current} onChange={e=>setPwd({...pwd,current:e.target.value})} />
        <Input label="Nouveau mot de passe" type="password" value={pwd.new} onChange={e=>setPwd({...pwd,new:e.target.value})} />
        <Input label="Confirmer" type="password" value={pwd.confirm} onChange={e=>setPwd({...pwd,confirm:e.target.value})} />
        <Btn onClick={changePwd}>Mettre à jour</Btn>
      </Card>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
function AppShell() {
  const { user, loading, isAdmin } = useAuth();
  const [nav,      setNav]      = useState("dashboard");
  const [sideOpen, setSideOpen] = useState(true);
  const [clients,  setClients]  = useState([]);
  const [toast,    setToast]    = useState(null);

  useEffect(() => { injectStyles(); }, []);

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3500); };

  useEffect(() => {
    if (user) api("/api/clients").then(d=>setClients(d.clients||[])).catch(()=>{});
  }, [user]);

  if (loading) return (
    <div style={{ height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16 }}>
      <div style={{ width:44,height:44,borderRadius:12,background:"var(--grad)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Montserrat',sans-serif",fontWeight:800,fontSize:18,color:"#fff" }}>N</div>
      <div style={{ color:"var(--muted)",fontSize:13 }}>Chargement...</div>
    </div>
  );

  if (!user) return <LoginPage />;

  const PAGE_TITLES = { dashboard:"Vue d'ensemble", compose:"Composer un post", calendar:"Calendrier éditorial", inbox:"Inbox unifié", bulk:"Bulk Scheduling", analytics:"Analytics & Reports", clients:"Gestion des clients", team:"Équipe & Accès", settings:"Paramètres" };

  return (
    <div style={{ display:"flex",height:"100vh",width:"100vw",overflow:"hidden" }}>
      {toast && <Toast msg={toast.msg} type={toast.type} />}
      <Sidebar nav={nav} setNav={setNav} sideOpen={sideOpen} setSideOpen={setSideOpen} />
      <div style={{ flex:1,display:"flex",flexDirection:"column",overflow:"hidden" }}>
        {/* TOPBAR */}
        <header style={{ height:56,background:"var(--dark2)",borderBottom:"1px solid var(--border2)",display:"flex",alignItems:"center",padding:"0 22px",gap:14,flexShrink:0 }}>
          <div style={{ flex:1,fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:16 }}>{PAGE_TITLES[nav]||nav}</div>
          <div style={{ position:"relative",width:34,height:34,borderRadius:8,background:"var(--dark3)",border:"1px solid var(--border2)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer" }}>
            <span style={{ fontSize:15 }}>🔔</span>
            <div style={{ position:"absolute",top:7,right:7,width:6,height:6,borderRadius:"50%",background:"var(--teal)",border:"2px solid var(--dark2)" }} />
          </div>
          <Btn onClick={()=>setNav("compose")}>+ Nouveau post</Btn>
        </header>
        {/* CLIENT BAR */}
        <div style={{ background:"var(--dark2)",borderBottom:"1px solid var(--border2)",padding:"8px 22px",display:"flex",alignItems:"center",gap:8,overflowX:"auto",flexShrink:0 }}>
          {[{ id:"all",name:"Tous les clients",color:"var(--teal)" },...clients].map(c=>(
            <div key={c.id} style={{ display:"flex",alignItems:"center",gap:6,padding:"5px 14px",borderRadius:20,cursor:"pointer",fontSize:12,fontWeight:500,whiteSpace:"nowrap",flexShrink:0,transition:"all 0.15s",background:"transparent",border:"1px solid var(--border2)",color:"var(--muted)" }}
              onClick={()=>setNav("clients")}>
              <div style={{ width:7,height:7,borderRadius:"50%",background:c.color||"var(--teal)",flexShrink:0 }} />{c.name}
            </div>
          ))}
          <Btn size="sm" variant="ghost" onClick={()=>setNav("clients")}>+ Client</Btn>
        </div>
        {/* CONTENT */}
        <main style={{ flex:1,overflowY:"auto",padding:22,background:"var(--dark)" }}>
          {nav==="dashboard" && <Dashboard clients={clients} toast={showToast} />}
          {nav==="compose"   && <ComposePage clients={clients} toast={showToast} />}
          {nav==="calendar"  && <CalendarPage clients={clients} />}
          {nav==="inbox"     && <InboxPage clients={clients} toast={showToast} />}
          {nav==="bulk"      && <BulkPage toast={showToast} />}
          {nav==="analytics" && <AnalyticsPage clients={clients} toast={showToast} />}
          {nav==="clients"   && <ClientsPage clients={clients} setClients={setClients} toast={showToast} />}
          {nav==="team"      && isAdmin() && <TeamPage toast={showToast} />}
          {nav==="settings"  && <SettingsPage toast={showToast} />}
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return <AuthProvider><AppShell /></AuthProvider>;
}
